import { useState, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Device, DeviceStatus, DeviceUpdatePayload, DeviceBulkUpdatePayload } from '../types/device';
import { 
  fetchDevices, 
  updateDeviceStatus, 
  updateDeviceProject, 
  bulkUpdateDevices,
  deviceReboot,
  deviceFirmwareUpdate
} from '../../../redux/device/actions';

export const useDeviceManagement = () => {
  const dispatch = useDispatch();
  const { devices, devicesLoading, devicesError } = useSelector((state: any) => state.deviceData);
  const [selectedDevices, setSelectedDevices] = useState<string[]>([]);
  const [lastFetchTime, setLastFetchTime] = useState<Date | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isManualRefresh, setIsManualRefresh] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const handleDeviceSelect = useCallback((deviceId: string) => {
    setSelectedDevices(prev => 
      prev.includes(deviceId) 
        ? prev.filter(id => id !== deviceId)
        : [...prev, deviceId]
    );
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedDevices(prev => 
      prev.length === devices.length ? [] : devices.map((d: Device) => d.id)
    );
  }, [devices]);

  const updateDevice = useCallback((deviceId: string, update: DeviceUpdatePayload) => {
    if (update.status) {
      dispatch(updateDeviceStatus({ deviceId, status: update.status }));
    }
    if (update.project !== undefined) {
      dispatch(updateDeviceProject({ deviceId, project: update.project }));
    }
  }, [dispatch]);

  const handleBulkUpdateDevices = useCallback((payload: DeviceBulkUpdatePayload) => {
    dispatch(bulkUpdateDevices(payload));
  }, [dispatch]);

  const handleDeviceReboot = useCallback(async (deviceId: string) => {
    try {
      dispatch(deviceReboot({ deviceId }));
    } catch (error) {
    }
  }, [dispatch]);

  const handleFirmwareUpdate = useCallback(async (deviceId: string) => {
    try {
      const device = devices.find((d: Device) => d.id === deviceId);
      if (device) {
        const currentVersion = parseFloat(device.metadata.firmware.slice(1));
        const newVersion = `v${(currentVersion + 0.1).toFixed(1)}.0`;
        dispatch(deviceFirmwareUpdate({ deviceId, firmwareVersion: newVersion }));
      }
    } catch (error) {
    }
  }, [devices, dispatch]);

  // Auto-refresh functionality
  const startAutoRefresh = useCallback(() => {
    // Clear existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    
    // Set up new interval for 20 seconds (20000 ms)
    intervalRef.current = setInterval(() => {
      // This is automatic timer fetch - no loading state
      dispatch(fetchDevices({}));
      setLastFetchTime(new Date());
    }, 20 * 1000);
  }, [dispatch]);

  const stopAutoRefresh = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const manualRefresh = useCallback(() => {
    setIsManualRefresh(true);
    dispatch(fetchDevices({}));
    setLastFetchTime(new Date());
    // Restart the auto-refresh timer
    startAutoRefresh();
  }, [dispatch, startAutoRefresh]);

  // Track loading state changes
  useEffect(() => {
    if (!devicesLoading) {
      // Reset flags when loading completes
      setIsInitialLoad(false);
      setIsManualRefresh(false);
    }
  }, [devicesLoading]);

  // Initialize auto-refresh on mount
  useEffect(() => {
    // Initial fetch
    dispatch(fetchDevices({}));
    setLastFetchTime(new Date());
    
    // Start auto-refresh
    startAutoRefresh();

    // Cleanup on unmount
    return () => {
      stopAutoRefresh();
    };
  }, [dispatch, startAutoRefresh, stopAutoRefresh]);

  return {
    devices,
    selectedDevices,
    loading: devicesLoading && (isInitialLoad || isManualRefresh),
    error: devicesError,
    lastFetchTime,
    isInitialLoad,
    isManualRefresh,
    handleDeviceSelect,
    handleSelectAll,
    updateDevice,
    bulkUpdateDevices: handleBulkUpdateDevices,
    handleDeviceReboot,
    handleFirmwareUpdate,
    manualRefresh,
    startAutoRefresh,
    stopAutoRefresh
  };
}; 